import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { SparkIcon } from '../SparkIcon/SparkIcon';
import './NavigationActionLink.css';

export interface NavigationActionLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Spark icon slug rendered before the label at 24px (e.g. "discord"). */
  iconSlug?: string;
  /** Opens `href` in a new tab with `rel="noopener noreferrer"`. Defaults to `true`. */
  external?: boolean;
  /**
   * Collapse to a square icon button (phone / tablet). The label stays in the
   * accessible name (visually hidden) and becomes the tooltip when it is a
   * string and no `title` is given. Requires `iconSlug`.
   */
  iconOnly?: boolean;
  /** Button label. Single line; overflows with an ellipsis. */
  children: ReactNode;
}

/**
 * Labelled action button for the Main Navigation's right-side actions cluster
 * (Figma "Button Main", medium, on the inverse surface). Renders an anchor so it
 * can point at an external destination such as a community invite.
 */
export const NavigationActionLink = forwardRef<HTMLAnchorElement, NavigationActionLinkProps>(
  function NavigationActionLink(
    { iconSlug, external = true, iconOnly = false, children, className = '', title, ...rest },
    ref,
  ) {
    const classes = [
      'spark-navigation-action-link',
      iconOnly ? 'spark-navigation-action-link--icon-only' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const tooltip = title ?? (iconOnly && typeof children === 'string' ? children : undefined);
    return (
      <a
        ref={ref}
        className={classes}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        title={tooltip}
        {...rest}
      >
        {iconSlug ? (
          <span className="spark-navigation-action-link-icon" aria-hidden="true">
            <SparkIcon slug={iconSlug} size="medium" />
          </span>
        ) : null}
        <span className="spark-navigation-action-link-label">{children}</span>
      </a>
    );
  },
);
